/**
 * Sub-projects take their parent's client when they have none of their own.
 *
 * Todoist has no clients, so a sub-project synced from it starts with none,
 * even when its parent is plainly one client's work. This finds every project
 * with no client whose nearest ancestor has one, and says what to copy down.
 * A sub-project with a client of its own keeps it: it may be billed to someone
 * else, and filling in is never allowed to overwrite.
 *
 * Copied, not looked up at read time, because rates resolve from the project
 * alone (hooks/useTime resolveClient) in many places that never see the tree.
 *
 * Pure: imports types only, so tests/client-inheritance.test.mjs loads it directly.
 */

import type { Project } from "./types";

type P = Pick<Project, "id" | "name" | "parent" | "client" | "clientRef" | "kind">;

export interface ClientFix {
  id: string;
  name: string;
  client: string;
  clientRef: string | null;
}

const hasClient = (p: P) => !!(p.clientRef || (p.client ?? "").trim());

export function inheritedClientFixes(projects: P[]): ClientFix[] {
  const byId = new Map(projects.map((p) => [p.id, p]));
  const fixes: ClientFix[] = [];
  for (const p of projects) {
    if (p.kind === "inbox" || hasClient(p) || !p.parent) continue;
    // Walk up to the nearest ancestor with a client. `seen` guards a loop.
    const seen = new Set([p.id]);
    let up = byId.get(p.parent);
    while (up && !hasClient(up) && up.parent && !seen.has(up.id)) {
      seen.add(up.id);
      up = byId.get(up.parent);
    }
    if (up && hasClient(up)) {
      fixes.push({ id: p.id, name: p.name, client: (up.client ?? "").trim(), clientRef: up.clientRef || null });
    }
  }
  return fixes;
}
